import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, PencilSimple, Power } from '@phosphor-icons/react';
import ExportButton from '../../components/ExportButton';
import SearchableSelect from '../../components/SearchableSelect';
import SuperAdminDeleteButton from '../../components/SuperAdminDeleteButton';

const PILOT_EXPORT_COLUMNS = [
  { key: 'emp_id', label: 'Employee ID' },
  { key: 'name', label: 'Name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email' },
  { key: 'license_type', label: 'License Type' },
  { key: 'license_valid_until', label: 'License Valid Until' },
  { key: 'vehicle_types', label: 'Vehicle Types', format: r => (r.vehicle_types || []).join('; ') },
  { key: 'allocation_mode', label: 'Allocation Mode' },
  { key: 'halt_location', label: 'Halt Location' },
  { key: 'can_self_add_trip', label: 'Self New Trip', format: r => r.can_self_add_trip === false ? 'No' : 'Yes' },
  { key: 'language', label: 'Language' },
  { key: 'status', label: 'Status', format: r => r.status || 'active' },
];

export default function PilotManagement() {
  const [pilots, setPilots] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', emp_id: '', mobile: '', email: '', license_type: '', license_valid_until: '', vehicle_types: [], allocation_mode: 'auto', breakfast_duration: 60, lunch_duration: 60, halt_location: '', can_self_add_trip: true, language: 'en', password: '' });

  useEffect(() => { fetchPilots(); fetchLicenseTypes(); fetchGeofences(); }, []);

  const fetchPilots = async () => {
    try { const { data } = await api.get('/admin/pilots'); setPilots(data); } catch {}
  };
  const fetchLicenseTypes = async () => {
    try { const { data } = await api.get('/admin/license-types'); setLicenseTypes(data); } catch {}
  };
  const fetchGeofences = async () => {
    try { const { data } = await api.get('/admin/geofences'); setGeofences(data.filter(g => (g.status || 'active') === 'active')); } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/pilots/${editing.id}`, form);
      } else {
        await api.post('/admin/pilots', form);
      }
      fetchPilots();
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', emp_id: '', mobile: '', email: '', license_type: '', license_valid_until: '', vehicle_types: [], allocation_mode: 'auto', breakfast_duration: 60, lunch_duration: 60, halt_location: '', can_self_add_trip: true, language: 'en', password: '' });
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleEdit = (pilot) => {
    setEditing(pilot);
    setForm({ ...pilot, can_self_add_trip: pilot.can_self_add_trip !== false, password: '' });
    setDialogOpen(true);
  };

  const handleToggle = async (id) => {
    await api.put(`/admin/toggle-status/pilots/${id}`);
    fetchPilots();
  };

  return (
    <div data-testid="pilot-management" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Pilots</h1>
          <p className="text-[#666] text-sm mt-1">{pilots.length} registered pilots</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton rows={pilots} columns={PILOT_EXPORT_COLUMNS} filename="pilots" testId="export-pilots-btn" />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-pilot-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={() => { setEditing(null); setForm({ name: '', emp_id: '', mobile: '', email: '', license_type: '', license_valid_until: '', vehicle_types: [], allocation_mode: 'auto', breakfast_duration: 60, lunch_duration: 60, halt_location: '', can_self_add_trip: true, language: 'en', password: '' }); }}>
              <Plus size={16} className="mr-2" /> Add Pilot
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold tracking-tight">{editing ? 'Edit Pilot' : 'Add New Pilot'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Name *</Label>
                <Input data-testid="pilot-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Employee ID</Label>
                <Input data-testid="pilot-empid-input" value={form.emp_id} onChange={e => setForm({...form, emp_id: e.target.value})} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Mobile * (Login Username)</Label>
                <Input value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} className="rounded-none" required placeholder="e.g. 9876543210" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">License Type</Label>
                <SearchableSelect
                  value={form.license_type}
                  onChange={v => setForm({...form, license_type: v})}
                  options={licenseTypes.map(lt => ({ value: lt.name, label: lt.name }))}
                  placeholder="Select license type"
                  searchPlaceholder="Search license type..."
                  emptyText="No matching license types"
                  allowClear
                  testId="license-type-select"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">License Valid Until</Label>
                <Input type="date" value={form.license_valid_until} onChange={e => setForm({...form, license_valid_until: e.target.value})} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Allocation Mode</Label>
                <Select value={form.allocation_mode} onValueChange={v => setForm({...form, allocation_mode: v})}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Self New Trip Adding</Label>
                <Select
                  value={form.can_self_add_trip === false ? 'no' : 'yes'}
                  onValueChange={v => setForm({...form, can_self_add_trip: v === 'yes'})}
                >
                  <SelectTrigger className="rounded-none" data-testid="pilot-self-add-trip-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes — pilot can add new trips</SelectItem>
                    <SelectItem value="no">No — pilot cannot add new trips</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Language</Label>
                <Select value={form.language} onValueChange={v => setForm({...form, language: v})}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ta">Tamil</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="ml">Malayalam</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Breakfast Duration (min)</Label>
                <Input type="number" value={form.breakfast_duration} onChange={e => setForm({...form, breakfast_duration: parseInt(e.target.value) || 60})} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Lunch Duration (min)</Label>
                <Input type="number" value={form.lunch_duration} onChange={e => setForm({...form, lunch_duration: parseInt(e.target.value) || 60})} className="rounded-none" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest">Halt Location</Label>
                <SearchableSelect
                  value={form.halt_location}
                  onChange={v => setForm({...form, halt_location: v})}
                  options={geofences.map(g => ({
                    value: g.halt_location || g.name,
                    label: g.halt_location || g.name,
                    hint: g.address || '',
                  }))}
                  placeholder="Select halt location"
                  searchPlaceholder="Search halt location..."
                  emptyText="No matching halt locations"
                  allowClear
                  testId="halt-location-select"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest">{editing ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
                <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={editing ? 'Enter new password to change' : 'Set login password'} className="rounded-none" required={!editing} />
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Cancel</Button>
                <Button data-testid="pilot-save-btn" type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Emp ID</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Mobile</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">License</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Allocation</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Self Trip</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pilots.map((p, i) => (
              <TableRow key={p.id} className={i % 2 === 0 ? '' : 'bg-[#F3F3F3]/30'}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.emp_id}</TableCell>
                <TableCell>{p.mobile}</TableCell>
                <TableCell>{p.license_type}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${p.allocation_mode === 'auto' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-[#FF8C00]/10 text-[#FF8C00]'}`}>
                    {p.allocation_mode}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    data-testid={`pilot-self-add-trip-${p.id}`}
                    className={`text-xs uppercase tracking-widest px-2 py-1 ${p.can_self_add_trip === false ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#00C853]/10 text-[#00C853]'}`}
                  >
                    {p.can_self_add_trip === false ? 'No' : 'Yes'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${p.status === 'active' ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FF3333]/10 text-[#FF3333]'}`}>
                    {p.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button onClick={() => handleEdit(p)} className="p-2 hover:bg-[#F3F3F3] transition-colors" data-testid={`edit-pilot-${p.id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => handleToggle(p.id)} className={`p-2 transition-colors ${p.status === 'inactive' ? 'hover:bg-[#00C853]/10 text-[#00C853]' : 'hover:bg-[#FF3333]/10 text-[#FF3333]'}`} title={p.status === 'inactive' ? 'Activate' : 'Deactivate'} data-testid={`toggle-pilot-${p.id}`}><Power size={16} /></button>
                  <SuperAdminDeleteButton masterType="pilots" recordId={p.id} recordName={p.name} onDeleted={fetchPilots} />
                </TableCell>
              </TableRow>
            ))}
            {pilots.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-[#666] py-8">No pilots registered yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
